import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../AppContext';
import { httpsCallable } from 'firebase/functions';
import { Sparkles, Flag, Trash2, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import HoverTooltip from './HoverTooltip';
import LoadingSpinner from './LoadingSpinner'; // Import the LoadingSpinner component

const AiToolsButton = ({ content, entryId, authorId, contextType, onAnalysisComplete, onReport, onDelete, canDelete, onShareToVision }) => {
    const { t } = useTranslation();
    const { appFunctions, showToast, showConfirmation } = useAppContext();
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [popupPosition, setPopupPosition] = useState('top-right');
    const menuRef = useRef(null);
    const buttonRef = useRef(null);

    const allTools = [
        { name: t('aiTools.explain'), type: 'AI_EXPLAIN', cost: 25 },
        { name: t('aiTools.suggestReply'), type: 'SUGGEST_COMMENT_REPLY', cost: 15 },
        { name: t('aiTools.summarize'), type: 'PUBLIC_SUMMARY', cost: 30 },
        { name: t('aiTools.keyPoints'), type: 'KEY_POINTS', cost: 25 },
        { name: t('aiTools.shiftPerspective'), type: 'PERSPECTIVE_SHIFT', cost: 35 },
        { name: t('aiTools.suggestQuestion'), type: 'SUGGEST_QUESTION', cost: 20 },
        { name: t('aiTools.analyzeTone'), type: 'TONE_ANALYSIS', cost: 20 },
        { name: t('aiTools.getVibe'), type: 'GET_VIBE', cost: 50, requiresEntryId: true },
        { name: t('aiTools.getAuraInsight'), type: 'GET_AURA_INSIGHT', cost: 100, requiresAuthorId: true },
    ];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target) && !buttonRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleToggleMenu = () => {
        if (!isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const menuWidth = 224; // Corresponds to w-56 in Tailwind (14rem * 16px/rem)
            let pos = '';

            // Set vertical position
            if (rect.top < 300) { // Simple check: if button is in top part of screen, open menu downwards
                pos += 'bottom';
            } else { // Otherwise, open upwards
                pos += 'top';
            }

            // Set horizontal position based on available space
            if ((viewportWidth - rect.left) < menuWidth) {
                // Not enough space on the right, align to the right edge
                pos += '-right';
            } else {
                // Enough space on the right, align to the left edge
                pos += '-left';
            }
            setPopupPosition(pos);
        }
        setIsOpen(!isOpen);
    };
    
    const handleToolSelect = (tool) => {
        showConfirmation({
            title: t('aiTools.confirmation.title'),
            message: t('aiTools.confirmation.message', { cost: tool.cost }),
            onConfirm: async () => {
                setIsLoading(true);
                setIsOpen(false);
                try {
                    const getAnalysis = httpsCallable(appFunctions, 'getAiAnalysis');
                    const response = await getAnalysis({ 
                        analysisType: tool.type, 
                        content, 
                        entryId,
                        authorId,
                        contextType
                    });
                    onAnalysisComplete(tool.type, response.data);
                } catch (error) {
                    showToast(error.message || t('aiTools.errors.unexpected'), "error");
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const handleActionClick = (action) => {
        setIsOpen(false);
        action();
    };

    const popupClasses = `absolute w-56 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50 ${
        popupPosition.includes('top') ? 'bottom-full mb-2' : 'top-full mt-2'
    } ${
        popupPosition.includes('right') ? 'right-0' : 'left-0'
    }`;

    return (
        <div className="relative inline-block" ref={menuRef}>
            <HoverTooltip text={t('aiTools.tooltip')}>
                <button 
                    ref={buttonRef}
                    onClick={handleToggleMenu} 
                    className="ai-icon-button text-yellow-400 hover:bg-yellow-400/20" 
                    aria-label={t('aiTools.ariaLabel')}
                    disabled={isLoading}
                >
                    {isLoading ? <LoadingSpinner size={20} /> : <Sparkles size={20} />}
                </button>
            </HoverTooltip>
            
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className={popupClasses}
                    >
                        <div className="py-1">
                            {allTools.map((tool) => (
                                <button
                                    key={tool.type}
                                    onClick={() => handleToolSelect(tool)}
                                    className="flex items-center w-full text-left px-3 py-2 text-sm text-gray-200 hover:bg-gray-800"
                                    disabled={isLoading}
                                >
                                    {tool.name} 
                                    <span className="ml-auto text-xs text-gray-500">({tool.cost})</span>
                                </button>
                            ))}
                            {(onReport || onDelete) && <div className="my-1 border-t border-gray-700"></div>}
                            {onReport && (
                                <button onClick={() => handleActionClick(onReport)} className="flex items-center w-full text-left px-3 py-2 text-sm text-orange-400 hover:bg-gray-800 font-bold">
                                    <Flag size={14} className="mr-2" /> {t('aiTools.report')}
                                </button>
                            )}
                            {canDelete && onDelete && (
                                <button onClick={() => handleActionClick(onDelete)} className="flex items-center w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-gray-800 font-bold">
                                    <Trash2 size={14} className="mr-2" /> {t('aiTools.delete')}
                                </button>
                            )}
                            {onShareToVision && (
                                <button onClick={() => handleActionClick(onShareToVision)} className="flex items-center w-full text-left px-3 py-2 text-sm text-blue-400 hover:bg-gray-800 font-bold">
                                    <Send size={14} className="mr-2" /> {t('aiTools.shareToVision')}
                                </button>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AiToolsButton;